import {AfterViewInit, Component, OnInit} from '@angular/core';
import {OverlayService} from "./overlay/overlay.service";
import {ActivatedRoute, NavigationEnd, NavigationStart, Router} from "@angular/router";
import {StoryService} from "./story/story.service";
import {UrlHashService} from "./services/url-hash.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {MetaTagsService} from "./services/meta-tags.service";
import {asyncScheduler, filter, map, mergeMap, observeOn, take} from "rxjs";
import {DataService} from "./services/data.service";
import {Store} from "@ngrx/store";
import {AppState} from "./store";
import {getArchiveSortState} from "./store/selectors/page.selector";
import {getUserSelfAssessedRequirementsTimeout} from "./store/selectors/user.selector";
import {
  clearAllSelfAssessedRequirements,
  resetSelfAssessedRequirementsTimeout,
  updateSelfAssessedRequirementsTimeout
} from "./store/actions/user.actions";
import {SelfAssessmentService} from "./user/self-assessment/self-assessment.service";
import {ProjectAreaService} from "./services/project-area.service";
import {ProjectArea} from "./enums/project-area";
import {PageLayout} from "./enums/page-layout";
import {FaviconService} from "./services/favicon.service";
import {StudentAuthService} from "./services/student-auth.service";
import {WatchDataService} from "./services/watch-data.service";
import {LargeMessageTheme} from "./enums/large-message-theme";
import {LargeMessageService} from "./services/large-message.service";
import {PageLayoutService} from "./services/page-layout.service";
import {AccessibilityService} from "./services/accessibility.service";
import {ViewportScroller} from "@angular/common";
import {UserInputModeDetectorService} from "./services/user-input-mode-detector.service";

declare let gtag: any;
declare let fbq: Function;

@UntilDestroy()
@Component({
  selector: 'brt-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements AfterViewInit, OnInit {

  showSubNavigation = true;
  showArchiveSort = false;
  showFooter = true;

  isGameArea = false;
  openedOverlays: number[] = [];

  currentPageLayout = PageLayout.UNKNOWN;

  lastPath = "/";

  constructor(
    private data: DataService,
    private overlayService: OverlayService,
    private router: Router,
    private storyService: StoryService,
    public route: ActivatedRoute,
    private urlHashService: UrlHashService,
    private metaTagsService: MetaTagsService,
    private store: Store<AppState>,
    private selfAssessmentService: SelfAssessmentService,
    private projectAreaService: ProjectAreaService,
    private faviconService: FaviconService,
    private studentAuthService: StudentAuthService,
    private watchDataService: WatchDataService,
    private largeMessageService: LargeMessageService,
    private pageLayoutService: PageLayoutService,
    private accessibilityService: AccessibilityService,
    private viewPortScroller: ViewportScroller,
    private userInputModeDetector: UserInputModeDetectorService,
  ) {
    this.watchDataService.getSchoolClassesByActiveUser();
    this.watchDataService.getUserInfo();
    this.watchDataService.getSchoolTeachers();
  }

  ngOnInit() {

    // TODO: Remove
    let currentCode = "";
    let timeoutBeforeClear = -1;

    window.document.addEventListener("keydown", ($event) => {
      currentCode += $event.key;
      // console.log(currentCode);

      switch (currentCode) {

        case "questunlock":
          // console.log("Sending unlock quest");
          break;

        case "stefanistheking":
          this.largeMessageService.showLargeMessage({
            theme: LargeMessageTheme.GOLD,
            headline: "Super, neue Trophäe <em>gesammelt!</em>",
            textTitle: "Quiz Profi",
            text: "Ihr habt in 3 Kategorien die letzte Stufe 12 geschafft.",
            primaryButtonLabel: "Ok",
            primaryButtonCallback: () => {},
            mainIcon: "/assets/img/dummy-assets/new-trophy.png",
            lottiePath: "/assets/placeholder.lottie",
            lottieLargeScale: true
          })
          break;
        case "stefanisthebeast":
          this.largeMessageService.showLargeMessage({
            theme: LargeMessageTheme.GOLD,
            headline: "Gratuliere,<br/> <em>neuer Heighscore!</em>",
            mainText: "1.200",
            primaryButtonLabel: "Erneut spielen",
            primaryButtonCallback: () => {alert("Not here")},
            secondaryButtonLabel: "Zurück yum Übersicht",
            secondaryButtonCallback: () => {alert("Not here")},
            lottiePath: "/assets/placeholder.lottie",
            lottieLargeScale: true
          })
          break;
        case "sophiagetsontop":
          const currentTime = new Date();
          this.largeMessageService.showLargeMessage({
            theme: LargeMessageTheme.GOLD,
            headline: "Gratuliere,<br/> <em>SOPHIA!</em>",
            mainText: `${currentTime.getHours()}:${currentTime.getMinutes().toString().padStart(2, '0')}`,
            text: "Der Arbeitstag ist fast vorbei",
            primaryButtonLabel: "Erneut arbeiten",
            primaryButtonCallback: () => {alert("Joke")},
            secondaryButtonLabel: "Zurück yum Übersicht",
            secondaryButtonCallback: () => {alert("Joke")},
            lottiePath: "/assets/placeholder.lottie",
            lottieLargeScale: true
          })
          break;
      }

      window.clearTimeout(timeoutBeforeClear);
      timeoutBeforeClear = window.setTimeout(() => {
        currentCode = "";
      }, 1000);
    })

    // Remove

    this.studentAuthService.logInUsingLocalStorage(); // on each page reload try to log in if local storage code exists

    this.projectAreaService.onAreaUpdated().subscribe((newArea) => {
      document.querySelector("body")?.setAttribute("data-project-area", newArea);
      this.isGameArea = newArea === ProjectArea.GAME;

    });

    if(localStorage.getItem("brt_privacy_set") != "true") {
      this.loadPrivacyWidget();
    }

    this.loadApiData();

    this.selfAssessmentService.showOnAppLoadIfNotVisited();

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.route),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route) => route.data)
    )
      .subscribe((event: any) => {
        // console.log(event);

        if(event.title) {
          this.metaTagsService.updateTitle(event.title, this.projectAreaService.getCurrentArea() === ProjectArea.ROOKIE);
        } else {
          this.metaTagsService.updateTitle("Die Welt der Berufe");
        }

      });

    // Trigger Route dependent Events
    this.router.events
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          // document.documentElement.style.scrollBehavior = "auto"; // smooth scroll if on same page, but fast scroll top if switch page (see navigation end)
        } else if (event instanceof NavigationEnd) {

          // Google Analytics Tracking
          // if(localStorage.getItem("brt_privacy_google_analytics") === "true") {
          //
          //   // @ts-ignore
          //   if(typeof window.dataLayer === "undefined") {
          //     // @ts-ignore
          //     window.dataLayer = window.dataLayer || [];
          //     (function(w: any,d: any,s: any,l: any,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          //         new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          //       j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          //       'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          //     })(window,document,'script','dataLayer','GTM-NNHT3WRJ');
          //   }
          //
          //   // @ts-ignore
          //   window.dataLayer.push({
          //     event: 'content-view',
          //     pageName: event.urlAfterRedirects
          //   })
          // }

          // @ts-ignore
          // if(window.GCM.apps.get("googleanalytics")) {
          //   /* Push pageview event for wko google analytics */
          //   // @ts-ignore
          //   window.dataLayer.push({
          //     event: 'pageview',
          //     pageUrl: event.urlAfterRedirects,
          //     pagePath: event.urlAfterRedirects,
          //     pageTitle: 'Berufsreise Tirol',
          //     pageAttributes: [{ id: "SVLEHR0006", groupId: "SVLEHR" }],
          //     pageServiceformats: [{ id: "SF313", groupId: "SFG3" }]
          //   });
          //
          //   /* Push Matomo tag manager event for berufsreise matomo */
          //   // @ts-ignore
          //   window._mtm.push({'event': 'mtm.PageView'});
          // }

          const finalUrl = event.urlAfterRedirects;
          this.setFavicon(finalUrl);

          const currentPath = finalUrl.split("#")[0];
          const previousPath = this.lastPath.split("#")[0];
          const currentParent = currentPath.split('/').slice(0, -1).join('/');
          const previousParent = previousPath.split('/').slice(0, -1).join('/');
          const isChildNavigation = currentParent === previousParent && currentParent.length > 0;

          let skipScroll = false;

          const currentPathSplit = currentPath.slice(1).split("/");
          //console.log(currentPathSplit);
          if(currentPathSplit.indexOf("schnupperplaetze") !== -1 && currentPathSplit.indexOf("rookie") !== -1 && currentPathSplit.length === 4) {
            skipScroll = true;
          }

          if(currentPath !== previousPath && !skipScroll) {
            window.scrollTo(0,0);
          }
          // this.showFooter = (this.route.snapshot.firstChild?.data["notFound"] ?? false) !== 'true';
          // this.showDefaultHeaderFooter = finalUrl.indexOf("infos-angebote") === -1;
          this.showSubNavigation = this.isSubNavigationVisible(finalUrl);
          // this.showArchiveSort = this.router.url === "/berufe" || this.router.url === "/anbieter" || this.router.url === "/jobboerse";
          this.urlHashService.tryOpen();

          this.handleSelfAssessmentSessionResults();


          // this.loopPageSettings(this.route);

          // this.setPageLayout(this.router.routerState.root);

          this.lastPath = finalUrl;
        }
      });

    // Overview components set this state in their ngOnInit, i.e. during this component's change detection,
    // so the value is applied asynchronously to avoid ExpressionChangedAfterItHasBeenCheckedError
    this.store.select(getArchiveSortState()).pipe(observeOn(asyncScheduler)).subscribe((val) => {
      this.showArchiveSort = val;
    })

    this.pageLayoutService.onLayoutChange().subscribe((val) => {
      this.currentPageLayout = val;
    })

    this.accessibilityService.getScaleValue().subscribe((newValue) => {
      window.document.querySelector("html")?.classList.toggle('a11y-font-scale-plus', newValue > 1);
      window.document.querySelector("html")?.classList.toggle('a11y-font-scale-minus', newValue < 1);
      window.document.querySelector("html")?.style.setProperty('--a11y-font-scale', newValue.toString());
    });

    this.accessibilityService.getTextScaleValue().subscribe((newValue) => {
      window.document.querySelector("html")?.classList.toggle('a11y-text-font-scale-plus', newValue > 1);
      window.document.querySelector("html")?.style.setProperty('--a11y-text-font-scale', newValue.toString());
    });

    this.accessibilityService.getGrayscaleValue().subscribe((newValue) => {
      window.document.querySelector("html")?.classList.toggle("a11y-grayscale", newValue);
    })

    this.accessibilityService.getHighContrastValue().subscribe((newValue) => {
      window.document.querySelector("html")?.classList.toggle("a11y-high-contrast", newValue);
    })

    this.accessibilityService.getStopMovingContent().subscribe((newValue) => {
      window.document.querySelector("html")?.classList.toggle("a11y-stop-moving-content", newValue);
    })

    this.accessibilityService.getTextSpacingValue().subscribe((newValue) => {
      window.document.querySelector("html")?.classList.toggle("a11y-text-spacing", newValue);
    })
  }

  ngAfterViewInit() {
  }

  loadApiData() {
    this.data.getAllJobRequirementTypes().subscribe(response => {});
  }

  handleSelfAssessmentSessionResults() {
    this.store.select(getUserSelfAssessedRequirementsTimeout()).pipe(take(1)).subscribe((val) => {
      if(val !== -1) {
        if(Date.now() > val) {
          this.store.dispatch(resetSelfAssessedRequirementsTimeout());
          this.store.dispatch(clearAllSelfAssessedRequirements());
        } else {
          this.store.dispatch(updateSelfAssessedRequirementsTimeout());
        }
      }
    })
  }

  loadPrivacyWidget() {
    setTimeout(() => {

    })
  }

  loopPageSettings(route: ActivatedRoute): void {
    // console.log(route);
    let newLayout = PageLayout.DEFAULT;
    let allRouteTree: ActivatedRoute[] = [];
    let myRoute: ActivatedRoute|null = route;
    while(myRoute !== null) {
      allRouteTree.push(myRoute);

      if('layout' in myRoute.snapshot.data) {
        newLayout = myRoute.snapshot.data["layout"] as PageLayout;
      }

      myRoute = myRoute.firstChild;
    }

    const lastTreeComponent = allRouteTree[allRouteTree.length - 1].component;

    allRouteTree = allRouteTree.filter(el => el.component === lastTreeComponent && el.component !== null);

    allRouteTree = allRouteTree.reverse();
    for(let i = 0; i < allRouteTree.length; i++) {
      // console.log(allRouteTree[i]);
      allRouteTree[i].data.pipe(take(1)).subscribe((val) => {
        // console.log(val);
      })
    }


    this.currentPageLayout = newLayout;
  }

  setFavicon(url: string): void {
    this.faviconService.setFaviconByUrl(url)
  }

  isSubNavigationVisible(url: string): boolean {
    const isHome = url === "/" || url.startsWith("/#");
    const isInfosOffers = url.indexOf("infos-angebote") !== -1;
    const isCompanyProfileSuggestion = url.startsWith("/vorschlag-unternehmensprofil");

    return !isHome && !isInfosOffers && !isCompanyProfileSuggestion && !this.isGameArea;
  }



  protected readonly PageLayout = PageLayout;

  skiplinkClicked($event: MouseEvent) {
    $event.preventDefault();

    const target = $event.target as HTMLLinkElement;
    console.log(target);
    this.viewPortScroller.scrollToAnchor(target.getAttribute("href") ?? "");
    (window.document.querySelector(target.getAttribute("href") ?? "") as HTMLElement).focus();
  }
}
